import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type React from 'react'
import { VB_W, VB_H, VIEW_MIN_X, VIEW_MIN_Y, VIEW_W, VIEW_H, TZ_AXIS_STRIP } from '@/views/worldView/geo'

export const MAX_ZOOM = 8
const ZOOM_STEP_BUTTON = 1.15
const ZOOM_STEP_WHEEL_IN = 1.08
const ZOOM_STEP_WHEEL_OUT = 0.92
/** Default = tight fit × two ＋ clicks so the world slightly overflows the edges. */
const DEFAULT_ZOOM_IN_CLICKS = 2

export type FitZoom = {
  zoom: number
  minZoom: number
  pan: { x: number; y: number }
}

function viewCenter() {
  return {
    vx: VIEW_MIN_X + VIEW_W / 2,
    vy: VIEW_MIN_Y + (VIEW_H - TZ_AXIS_STRIP) / 2,
  }
}

function viewBandH() {
  return VIEW_H - TZ_AXIS_STRIP
}

/**
 * Height-filling clamp: meridians must stay top–bottom at allowed zooms.
 * X allows only the intentional edge overflow — cannot pan the world away.
 */
export function clampPan(pan: { x: number; y: number }, zoom: number) {
  const z = Math.max(zoom, 0.001)
  const mapW = VB_W * z
  const mapH = VB_H * z
  const bandH = viewBandH()

  // Y: keep map covering the full band height (no vertical gaps for meridians).
  let minY: number
  let maxY: number
  if (mapH >= bandH) {
    minY = VIEW_MIN_Y + bandH - mapH
    maxY = VIEW_MIN_Y
  } else {
    // Should not happen at MIN_ZOOM; center if undersized.
    const y = VIEW_MIN_Y + (bandH - mapH) / 2
    minY = maxY = y
  }

  // X: keep at least ~70% of the viewport filled with map.
  const overflowX = Math.max(0, mapW - VIEW_W)
  const minX = VIEW_MIN_X + VIEW_W - mapW - overflowX * 0.05
  const maxX = VIEW_MIN_X + overflowX * 0.05

  return {
    x: Math.min(Math.max(minX, pan.x), Math.max(minX, maxX)),
    y: Math.min(Math.max(minY, pan.y), Math.max(minY, maxY)),
  }
}

function clampZoomToFit(z: number, minZoom: number, maxZoom = MAX_ZOOM) {
  return Math.max(minZoom, Math.min(maxZoom, z))
}

/**
 * Fit so the full map is in view with slight edge overflow at default zoom.
 * minZoom = one − click from default; cannot zoom out further.
 */
export function computeDataFit(points: ReadonlyArray<{ x: number; y: number }> = []): FitZoom {
  const { vx, vy } = viewCenter()
  const bandH = viewBandH()

  // Base: scale so the SVG world just fills the view (slight overflow via ×1.15²).
  const fillX = VIEW_W / VB_W
  const fillY = bandH / VB_H
  const cover = Math.max(fillX, fillY) // cover viewport → edges overflow on the other axis
  const defaultZoom = clampZoomToFit(cover * Math.pow(ZOOM_STEP_BUTTON, DEFAULT_ZOOM_IN_CLICKS), 0.5)
  const minZoom = defaultZoom / ZOOM_STEP_BUTTON

  // If we have data points, prefer centering on them while keeping zoom in [min, default*max].
  let cx = VB_W / 2
  let cy = VB_H / 2
  if (points.length) {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const p of points) {
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x)
      maxY = Math.max(maxY, p.y)
    }
    cx = (minX + maxX) / 2
    cy = (minY + maxY) / 2
  }

  const zoom = defaultZoom
  return {
    zoom,
    minZoom,
    pan: clampPan({ x: vx - cx * zoom, y: vy - cy * zoom }, zoom),
  }
}

export function usePanZoom() {
  const initial = computeDataFit([])
  const [zoom, setZoom] = useState(initial.zoom)
  const [pan, setPan] = useState(initial.pan)
  const [isPanning, setIsPanning] = useState(false)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const dragRef = useRef<null | { start: { x: number; y: number }; pan0: { x: number; y: number } }>(null)
  const fitPointsRef = useRef<ReadonlyArray<{ x: number; y: number }>>([])
  const minZoomRef = useRef(initial.minZoom)
  const defaultZoomRef = useRef(initial.zoom)

  const clampZ = useCallback((z: number) => clampZoomToFit(z, minZoomRef.current, MAX_ZOOM), [])

  const screenToSvg = useCallback(
    (e: Pick<React.PointerEvent<SVGSVGElement>, 'clientX' | 'clientY'>) => {
      const svg = svgRef.current
      if (!svg) return { x: 0, y: 0 }
      const pt = svg.createSVGPoint()
      pt.x = e.clientX
      pt.y = e.clientY
      const ctm = svg.getScreenCTM()
      if (!ctm) return { x: 0, y: 0 }
      const p = pt.matrixTransform(ctm.inverse())
      return { x: p.x, y: p.y }
    },
    [],
  )

  const applyZoomAroundCenter = useCallback((nextRaw: number) => {
    const { vx, vy } = viewCenter()
    setZoom((z) => {
      const next = clampZ(nextRaw)
      setPan((p) => {
        const cx = (vx - p.x) / Math.max(z, 0.001)
        const cy = (vy - p.y) / Math.max(z, 0.001)
        return clampPan({ x: vx - cx * next, y: vy - cy * next }, next)
      })
      return next
    })
  }, [clampZ])

  // Native non-passive wheel so preventDefault actually blocks page scroll.
  useLayoutEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const dz = e.deltaY > 0 ? ZOOM_STEP_WHEEL_OUT : ZOOM_STEP_WHEEL_IN
      setZoom((z) => {
        const next = clampZ(z * dz)
        const { vx, vy } = viewCenter()
        setPan((p) => {
          const cx = (vx - p.x) / Math.max(z, 0.001)
          const cy = (vy - p.y) / Math.max(z, 0.001)
          return clampPan({ x: vx - cx * next, y: vy - cy * next }, next)
        })
        return next
      })
    }
    svg.addEventListener('wheel', onWheel, { passive: false })
    return () => svg.removeEventListener('wheel', onWheel)
  })

  const zoomIn = useCallback(() => {
    applyZoomAroundCenter(zoom * ZOOM_STEP_BUTTON)
  }, [applyZoomAroundCenter, zoom])

  const zoomOut = useCallback(() => {
    applyZoomAroundCenter(zoom / ZOOM_STEP_BUTTON)
  }, [applyZoomAroundCenter, zoom])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (e.target !== e.currentTarget) return
      dragRef.current = { start: screenToSvg(e), pan0: pan }
      setIsPanning(true)
      ;(e.currentTarget as SVGSVGElement).setPointerCapture?.(e.pointerId)
    },
    [pan, screenToSvg],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const d = dragRef.current
      if (!d) return
      const cur = screenToSvg(e)
      setPan(clampPan({
        x: d.pan0.x + (cur.x - d.start.x),
        y: d.pan0.y + (cur.y - d.start.y),
      }, zoom))
    },
    [screenToSvg, zoom],
  )

  const onPointerUp = useCallback(() => {
    dragRef.current = null
    setIsPanning(false)
  }, [])

  const fitToData = useCallback((points: ReadonlyArray<{ x: number; y: number }>) => {
    fitPointsRef.current = points
    const next = computeDataFit(points)
    minZoomRef.current = next.minZoom
    defaultZoomRef.current = next.zoom
    setZoom(next.zoom)
    setPan(next.pan)
  }, [])

  const reset = useCallback(() => {
    const next = computeDataFit(fitPointsRef.current)
    minZoomRef.current = next.minZoom
    defaultZoomRef.current = next.zoom
    setZoom(next.zoom)
    setPan(next.pan)
  }, [])

  const zoomTo = useCallback((mapXY: { x: number; y: number }, targetZoom?: number) => {
    const { vx, vy } = viewCenter()
    setZoom((z) => {
      const next = clampZ(targetZoom ?? z * ZOOM_STEP_BUTTON * ZOOM_STEP_BUTTON)
      setPan(clampPan({
        x: vx - mapXY.x * next,
        y: vy - mapXY.y * next,
      }, next))
      return next
    })
  }, [clampZ])

  return {
    zoom,
    pan,
    isPanning,
    svgRef,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
    controls: { zoomIn, zoomOut, reset, zoomTo, fitToData },
  }
}
